import React, {FC} from 'react';
import styled from 'styled-components';

import {PaletteColorsEnum} from '../../../libs/shared/core/src/models/api/paletteModel';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {StartGuideTaskDefinition, StartGuideTaskInteractionsEnum} from './startGuideCommon';

/*
 * Style.
 */

const StyledBaseTaskButton = styled(ChromelessButton)`
  padding: 12px;
  border-radius: 100px;
`;

const StyledCompletedTaskButton = styled(StyledBaseTaskButton)`
  background: ${(p) => p.theme.palette[PaletteColorsEnum.GREEN].shade10};

  &:hover {
    background: ${(p) => p.theme.palette[PaletteColorsEnum.GREEN].shade20};
  }
`;

const StyledTaskButton = styled(StyledBaseTaskButton)`
  padding: 11px;
  border: 1px solid ${(p) => p.theme.alphas.black20};

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
  }
`;

const StyledTaskFirstLine = styled.div`
  display: grid;
  grid-template-columns: 26px auto 16px;

  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
`;

const StyledTaskTitle = styled.div`
  margin-right: 5px;
  ${ellipsis()}
`;

const StyledTaskCircle = styled.div`
  border-radius: 50%;
  border: 1px solid ${(p) => p.theme.greys.shade50};
  width: 16px;
  height: 16px;
`;

/*
 * Component.
 */

export const StartGuideTask: FC<StartGuideTaskDefinition> = (props) => {
  const theme = useUserTheme();

  // Rendering incomplete tasks
  if (props.isIncomplete) {
    return (
      <TooltipCoordinator placement="top" render={props.renderTooltip}>
        <StyledTaskButton onClick={props.onClick} interactionId={props.interactionId}>
          <StyledTaskFirstLine>
            <StyledTaskCircle />
            <StyledTaskTitle>{props.title}</StyledTaskTitle>
            <Icon name={props.iconName} size={16} />
          </StyledTaskFirstLine>
        </StyledTaskButton>
      </TooltipCoordinator>
    );
  }

  // Allow the user to click into settings even after the task is completed.
  const onClick = () => {
    if (props.interactionId === StartGuideTaskInteractionsEnum.EXPLORE_SETTINGS) {
      return props.onClick();
    }
    return undefined;
  };

  // Rendering completed tasks.
  return (
    <StyledCompletedTaskButton onClick={onClick}>
      <StyledTaskFirstLine>
        <Icon name="checkmarkCircleSmall" color={theme.palette[PaletteColorsEnum.GREEN].shade40} />
        <StyledTaskTitle>{props.title}</StyledTaskTitle>
        <Icon name={props.iconName} color={theme.palette[PaletteColorsEnum.GREEN].shade40} size={16} />
      </StyledTaskFirstLine>
    </StyledCompletedTaskButton>
  );
};
